import styled from '@emotion/styled';
import { CreditCard, PhoneAndroid } from '@mui/icons-material';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Switch,
    TextField,
    Tooltip,
    alpha
} from '@mui/material';
import { green, lightBlue, red } from '@mui/material/colors';
import { action, computed, makeObservable, observable, reaction } from 'mobx';
import { inject, observer } from 'mobx-react';
import { Fragment } from 'react';
import '../../css/certidao.css';
import Configuracao from '../../util/Configuracao';
import ReactComponent from '../ReactComponent';

const SwitchAssinatura = styled(Switch)(({ theme }) => ({
    '& .MuiSwitch-switchBase': {
        color: lightBlue[900],
        '&:hover': {
            // @ts-ignore
            backgroundColor: alpha(lightBlue[900], theme.palette.action.hoverOpacity)
        }
    },
    '& .MuiSwitch-switchBase.Mui-checked.Mui-disabled': {
        color: 'rgb(158, 191, 217)'
    },
    '& .MuiSwitch-switchBase.Mui-checked': {
        color: lightBlue[900],
        '&:hover': {
            // @ts-ignore
            backgroundColor: alpha(lightBlue[900], theme.palette.action.hoverOpacity)
        }
    },
    '& .MuiSwitch-switchBase + .MuiSwitch-track': {
        backgroundColor: lightBlue[900],
        opacity: 0.5
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
        backgroundColor: lightBlue[900],
        opacity: 0.5
    },
    '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.12
    }
}));

class ModoAssinatura extends ReactComponent {
    chaveOtp = '';

    constructor(props) {
        super(props);

        makeObservable(this, {
            chaveOtp: observable,
            handleChangeModoAssinatura: action,
            handleChangeChaveOtp: action,
            handleClickCancelar: action,
            handleClickContinuar: action,
            handleKeyUpCapture: action,
            existeProcessoPJeSelecionado: computed,
            modoAssinaturaDesabilitada: computed,
            renderIconeJTe: computed,
            renderIconeShodo: computed
        });

        this.createReaction(
            reaction(
                () => this.appStore.instalacaoShodoValida,
                () => {
                    if (!this.appStore.instalacaoShodoValida) {
                        this.sessaoStore.modoAssinatura = Configuracao.MODO_ASSINATURA_JTE;
                    }
                }
            )
        );
    }

    componentDidMount() {
        this.appStore.validarInstalacaoShodo();
    }

    handleChangeModoAssinatura = (checked) => {
        this.sessaoStore.modoAssinatura = checked
            ? Configuracao.MODO_ASSINATURA_JTE
            : Configuracao.MODO_ASSINATURA_SHODO;
    };

    handleChangeChaveOtp = (event) => {
        this.chaveOtp = event.target.value ? event.target.value.toString().substring(0, 6) : '';
    };

    handleClickCancelar = () => {
        this.chaveOtp = '';
        this.observableProps.callbackCancelar && this.observableProps.callbackCancelar();
    };

    handleClickContinuar = () => {
        this.observableProps.callbackContinuarAssinaturaOtp &&
            this.observableProps.callbackContinuarAssinaturaOtp(this.chaveOtp);
        this.chaveOtp = '';
    };

    handleKeyUpCapture = (event) => {
        if (event.key === 'Enter' && this.chaveOtp && this.chaveOtp.length === 6) {
            this.handleClickContinuar();
        }
    };

    get existeProcessoPJeSelecionado() {
        return (
            (this.observableProps.registro &&
                this.observableProps.registro.processo.origem === Configuracao.ORIGEM_PJE) ||
            (this.observableProps.processos &&
                this.observableProps.processos.findIndex(
                    (registro) => registro.processo.origem === Configuracao.ORIGEM_PJE
                ) !== -1)
        );
    }

    get modoAssinaturaDesabilitada() {
        return (
            !this.appStore.instalacaoShodoValida && this.sessaoStore.modoAssinatura === Configuracao.MODO_ASSINATURA_JTE
        );
    }

    render() {
        return (
            <Fragment>
                <div style={{ display: 'flex', alignItems: 'center', margin: '0px 8px' }}>
                    {this.renderIconeShodo}
                    <SwitchAssinatura
                        disabled={this.modoAssinaturaDesabilitada}
                        checked={this.sessaoStore.modoAssinatura === Configuracao.MODO_ASSINATURA_JTE}
                        onChange={(event) => this.handleChangeModoAssinatura(event.target.checked)}
                    />
                    {this.renderIconeJTe}
                </div>
                {this.observableProps.modalChaveOtpOpen && (
                    <Dialog
                        open
                        maxWidth="xl"
                    >
                        <DialogTitle>Assinatura com JTe</DialogTitle>
                        <DialogContent style={{ paddingTop: '10px', width: '500px' }}>
                            <TextField
                                fullWidth
                                autoComplete="off"
                                label="Informe o código de autorização exibido no aplicativo JTe"
                                value={this.chaveOtp}
                                onChange={this.handleChangeChaveOtp}
                                onKeyUpCapture={this.handleKeyUpCapture}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={this.handleClickCancelar}
                                color="secondary"
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={this.handleClickContinuar}
                                color="primary"
                                disabled={!this.chaveOtp || this.chaveOtp.length !== 6}
                            >
                                Continuar
                            </Button>
                        </DialogActions>
                    </Dialog>
                )}
            </Fragment>
        );
    }

    get renderIconeJTe() {
        return this.modoAssinaturaDesabilitada ? (
            <Tooltip
                placement="top"
                title={this.getTooltipAssinarJTe()}
            >
                <PhoneAndroid style={{ fill: green[800], fillOpacity: '0.7' }} />
            </Tooltip>
        ) : (
            <Tooltip
                placement="top"
                title={this.getTooltipAssinarJTe()}
            >
                <PhoneAndroid
                    style={{ cursor: 'pointer', fill: green[800], fillOpacity: '0.7' }}
                    onClick={() => this.handleChangeModoAssinatura(true)}
                />
            </Tooltip>
        );
    }

    getTooltipAssinarJTe() {
        return 'Assinar com JTe' + (this.existeProcessoPJeSelecionado ? ' (envio automático da certidão ao PJe)' : '');
    }

    get renderIconeShodo() {
        return this.appStore.instalacaoShodoValida ? (
            <Tooltip
                placement="top"
                title="Assinar com SHODO"
            >
                <CreditCard
                    style={{ cursor: 'pointer', fill: green[800], fillOpacity: '0.7' }}
                    onClick={() => this.handleChangeModoAssinatura(false)}
                />
            </Tooltip>
        ) : (
            <Tooltip
                placement="top"
                title="Assinador SHODO inacessível ou indisponível."
            >
                <CreditCard style={{ fill: red[800], fillOpacity: '0.7' }} />
            </Tooltip>
        );
    }
}

export default inject('stores')(observer(ModoAssinatura));
